'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { getDebugFlags } from '@/lib/debugFlags';

interface LocationResolvedPayload {
  viewport?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  center?: {
    lat: number;
    lng: number;
  };
  zoom?: number;
}

interface SearchBarClientProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  onLocationResolved?: (payload: LocationResolvedPayload) => void;
}

const AUTOCOMPLETE_DEBOUNCE = 150;

/** Bounded retry: check every intervalMs up to timeoutMs. Returns true when Places is available. */
function waitForGooglePlaces(
  timeoutMs: number,
  intervalMs: number,
  timeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
  mountedRef: React.MutableRefObject<boolean>
): Promise<boolean> {
  return new Promise((resolve) => {
    const start = Date.now();

    const check = (): void => {
      if (!mountedRef.current) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        resolve(false);
        return;
      }
      if (typeof window !== 'undefined' && window.google?.maps?.places) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        resolve(true);
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        timeoutRef.current = null;
        resolve(false);
        return;
      }
      timeoutRef.current = setTimeout(check, intervalMs);
    };

    check();
  });
}

export default function SearchBarClient({
  value = '',
  onChange,
  placeholder = 'Search city or address',
  onLocationResolved,
}: SearchBarClientProps) {
  const [q, setQ] = useState(value);
  const [suggestions, setSuggestions] = useState<Array<{
    place_id: string;
    description: string;
  }>>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [browserCompatible, setBrowserCompatible] = useState(false);
  const containerRef = useRef<HTMLFormElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<number | null>(null);
  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const isSelectingRef = useRef(false);
  const initTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    setQ(value);
  }, [value]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
    };
  }, []);

  // Initialize Google Places services exactly once per mount (no setInterval, bounded retry)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (getDebugFlags().noPlaces) return;

    void waitForGooglePlaces(5000, 75, initTimeoutRef, mountedRef).then((ready) => {
      if (!mountedRef.current || !ready) return;

      try {
        if (typeof window.google.maps.places.AutocompleteService === 'undefined') return;
        if (!serviceRef.current) {
          serviceRef.current = new window.google.maps.places.AutocompleteService();
        }
        if (!placesServiceRef.current) {
          const dummyDiv = document.createElement('div');
          placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);
        }
        if (mountedRef.current) {
          setBrowserCompatible(true);
          if (process.env.NODE_ENV !== 'production') {
            console.log('[SearchBarClient] Places services ready');
          }
        }
      } catch {
        // fail quietly
      }
    });
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
  }, []);

  const clearSuggestions = useCallback(() => {
    if (mountedRef.current) {
      setSuggestions([]);
      setActiveIndex(-1);
    }
  }, []);

  const fetchSuggestions = useCallback(
    (input: string) => {
      if (!serviceRef.current) {
        clearSuggestions();
        return;
      }

      try {
        serviceRef.current.getPlacePredictions(
          { input, types: ['geocode'] },
          (predictions, status) => {
            if (!mountedRef.current) return;
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              const formatted = predictions.slice(0, 5).map((p) => ({
                place_id: p.place_id,
                description: p.description,
              }));
              setSuggestions(formatted);
              setActiveIndex(-1);
            } else {
              clearSuggestions();
            }
          }
        );
      } catch {
        clearSuggestions();
      }
    },
    [clearSuggestions]
  );

  const debouncedFetch = useCallback(
    (input: string) => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      if (!input.trim()) {
        clearSuggestions();
        return;
      }
      if (browserCompatible && serviceRef.current) {
        debounceRef.current = window.setTimeout(() => fetchSuggestions(input), AUTOCOMPLETE_DEBOUNCE);
      } else {
        clearSuggestions();
      }
    },
    [clearSuggestions, fetchSuggestions, browserCompatible]
  );

  const resolveLocationFromPlaceId = useCallback(
    (placeId: string): Promise<LocationResolvedPayload | null> => {
      return new Promise((resolvePromise) => {
        if (!placesServiceRef.current) {
          resolvePromise(null);
          return;
        }

        placesServiceRef.current.getDetails(
          { placeId, fields: ['geometry', 'formatted_address', 'name'] },
          (place, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry) {
              const payload: LocationResolvedPayload = {};
              if (place.geometry.viewport) {
                const ne = place.geometry.viewport.getNorthEast();
                const sw = place.geometry.viewport.getSouthWest();
                payload.viewport = {
                  north: ne.lat(),
                  south: sw.lat(),
                  east: ne.lng(),
                  west: sw.lng(),
                };
              } else if (place.geometry.location) {
                const location = place.geometry.location;
                payload.center = { lat: location.lat(), lng: location.lng() };
                payload.zoom = 12;
              }
              resolvePromise(payload);
            } else {
              resolvePromise(null);
            }
          }
        );
      });
    },
    []
  );

  const resolveLocationFromText = useCallback(
    (text: string): Promise<LocationResolvedPayload | null> => {
      return new Promise((resolvePromise) => {
        if (typeof window === 'undefined' || !window.google?.maps) {
          resolvePromise(null);
          return;
        }
        try {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ address: text }, (results, status) => {
            if (status === window.google.maps.GeocoderStatus.OK && results && results.length > 0) {
              const result = results[0];
              const payload: LocationResolvedPayload = {};
              if (result.geometry.viewport) {
                const ne = result.geometry.viewport.getNorthEast();
                const sw = result.geometry.viewport.getSouthWest();
                payload.viewport = {
                  north: ne.lat(),
                  south: sw.lat(),
                  east: ne.lng(),
                  west: sw.lng(),
                };
              } else if (result.geometry.location) {
                payload.center = {
                  lat: result.geometry.location.lat(),
                  lng: result.geometry.location.lng(),
                };
                payload.zoom = 12;
              }
              resolvePromise(payload);
            } else {
              resolvePromise(null);
            }
          });
        } catch {
          resolvePromise(null);
        }
      });
    },
    []
  );

  const handleLocationResolved = useCallback(
    async (payload: LocationResolvedPayload | null) => {
      if (payload && onLocationResolved) {
        onLocationResolved(payload);
      }
    },
    [onLocationResolved]
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = q.trim();
    if (!trimmed) return;
    clearSuggestions();

    if (suggestions.length > 0) {
      handleSuggestionSelect(suggestions[0]);
      return;
    }

    void resolveLocationFromText(trimmed).then(handleLocationResolved);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value;
    setQ(newValue);
    setActiveIndex(-1);
    if (onChange) onChange(newValue);
    if (newValue.length >= 2) {
      if (browserCompatible && serviceRef.current) {
        debouncedFetch(newValue);
      } else {
        clearSuggestions();
      }
    } else {
      clearSuggestions();
    }
  }

  const handleSuggestionSelect = useCallback(
    async (suggestion: { place_id: string; description: string }) => {
      isSelectingRef.current = true;
      setQ(suggestion.description);
      if (onChange) onChange(suggestion.description);
      clearSuggestions();

      const payload = await resolveLocationFromPlaceId(suggestion.place_id);
      await handleLocationResolved(payload);

      setTimeout(() => {
        isSelectingRef.current = false;
      }, 100);
    },
    [clearSuggestions, onChange, resolveLocationFromPlaceId, handleLocationResolved]
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestions.length) {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSubmit(event as unknown as React.FormEvent);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (activeIndex >= 0) {
        handleSuggestionSelect(suggestions[activeIndex]);
      } else {
        if (suggestions.length > 0) {
          handleSuggestionSelect(suggestions[0]);
        } else {
          handleSubmit(event as unknown as React.FormEvent);
        }
      }
    } else if (event.key === 'Escape') {
      clearSuggestions();
    }
  };

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (!isSelectingRef.current) {
        clearSuggestions();
      }
    }, 200);
  }, [clearSuggestions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current) return;
      const target = event.target instanceof Node ? event.target : null;
      if (target && !containerRef.current.contains(target)) {
        clearSuggestions();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [clearSuggestions]);

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        maxWidth: '100%',
      }}
      ref={containerRef}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          minWidth: 0,
          flex: 1,
          overflow: 'visible',
        }}
      >
        <input
          ref={inputRef}
          value={q}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: '12px 16px',
            minHeight: '44px',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            fontSize: '16px',
            boxSizing: 'border-box',
            touchAction: 'manipulation',
            WebkitAppearance: 'none',
            MozAppearance: 'textfield',
          }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        {suggestions.length > 0 && (
          <ul
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              width: '100%',
              zIndex: 9999,
              marginTop: 4,
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              background: '#fff',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              maxHeight: '300px',
              overflowY: 'auto',
              overflowX: 'hidden',
              listStyle: 'none',
              margin: '4px 0 0 0',
              padding: 0,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.place_id}
                style={{
                  cursor: 'pointer',
                  padding: '12px 16px',
                  minHeight: '44px',
                  fontSize: '14px',
                  background: index === activeIndex ? '#eff6ff' : '#fff',
                  color: index === activeIndex ? '#1d4ed8' : '#111827',
                  borderBottom: index < suggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSuggestionSelect(suggestion);
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  handleSuggestionSelect(suggestion);
                }}
                onMouseEnter={() => setActiveIndex(index)}
              >
                {suggestion.description}
              </li>
            ))}
          </ul>
        )}
      </div>
      <button
        type="submit"
        style={{
          padding: '12px 20px',
          minHeight: '44px',
          minWidth: '80px',
          borderRadius: 8,
          background: '#111827',
          color: '#fff',
          border: 'none',
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
          touchAction: 'manipulation',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          WebkitAppearance: 'none',
          MozAppearance: 'none',
        }}
      >
        Search
      </button>
    </form>
  );
}
